import { Box } from "@chakra-ui/react";
import { OperacaoEnum } from "core/resources/enums/conta";
import { Control, UseFormSetValue } from "react-hook-form";
import InputToggleContent from "components/Input/InputToggleContent/index";
import Field from "components/Input/Field";
import FormLiquidarConta from "./FormLiquidarConta";
import { useDebounce } from "features/shared/useDebouce";
import { useEffect } from "react";
import { ContaForm } from "core/features/conta/typings";
import useFindEncargosAtraso from "core/features/conta/hooks/useFindEncargos";
import SkeletonForm from "./../SkeletonForm";
import moment from "moment";
interface LiquidarContaProps {
  control: Control<ContaForm, object>;
  operacao: OperacaoEnum;
  form?: ContaForm;
  setValue: UseFormSetValue<ContaForm>;
  totalSemEncargos: number;
  required?: boolean;
  isEdit?: boolean;
  fixedDataMovimentacao?: string;
}

function LiquidarConta({
  control,
  operacao,
  form,
  setValue,
  totalSemEncargos,
  required,
  isEdit,
  fixedDataMovimentacao,
}: LiquidarContaProps) {
  const debouncedDataPagamento = useDebounce(
    form?.pagarConta?.dataPagamento,
    150
  );

  const {
    data: dataEncargos,
    isLoading: isLoadingEncargos,
    isFetching: isFetchingEncargos,
  } = useFindEncargosAtraso(
    form?.dataVencimento,
    debouncedDataPagamento,
    totalSemEncargos,
    !!form?.liquidarConta && !!form?.calcularJurosAutomatico
  );

  useEffect(() => {
    setValue("pagarConta.valor", totalSemEncargos);
  }, [setValue, totalSemEncargos]);

  const _isLoading = isLoadingEncargos || isFetchingEncargos;

  useEffect(() => {
    if (form?.tenantContaId) {
      setValue("pagarConta.tenantContaId", form?.tenantContaId);
    }
  }, [setValue, form?.tenantContaId, _isLoading]);

  useEffect(() => {
    if (form?.dataVencimento) {
      setValue(
        "pagarConta.dataPagamento",
        isEdit ? moment().format("YYYY-MM-DD") : form?.dataVencimento
      );
    }
  }, [setValue, form?.dataVencimento, isEdit]);

  useEffect(() => {
    if (dataEncargos && form?.calcularJurosAutomatico) {
      setValue("pagarConta.juros", dataEncargos.juros);
      setValue("pagarConta.multa", dataEncargos.multa);
    }
  }, [dataEncargos, form?.calcularJurosAutomatico, setValue]);

  useEffect(() => {
    const valorPago =
      (form?.pagarConta?.valor || 0) +
      (form?.pagarConta?.juros || 0) +
      (form?.pagarConta?.multa || 0) +
      (form?.pagarConta?.taxaAdicionalCategoriaOperacao === 1
        ? form?.pagarConta?.taxaAdicional
        : 0);

    setValue("pagarConta.valorPago", valorPago);
  }, [
    form?.pagarConta?.valor,
    form?.pagarConta?.juros,
    form?.pagarConta?.multa,
    form?.pagarConta?.taxaAdicional,
    form?.pagarConta?.taxaAdicionalCategoriaOperacao,
    form?.composicoes,
    setValue,
  ]);

  return (
    <Box mb={2}>
      <Field
        shouldUnregister
        control={control}
        name="liquidarConta"
        render={({ field: toggle }) => (
          <InputToggleContent
            label={`Liquidar conta${required ? "*" : ""}`}
            active={!!toggle.value || !!required}
            onToggle={toggle.onChange}
            size="sm"
          >
            {_isLoading ? <SkeletonForm /> : null}

            {!_isLoading && (!!toggle.value || !!required) ? (
              <FormLiquidarConta
                control={control}
                isFetchingEncargos={isFetchingEncargos}
                isLoadingEncargos={isLoadingEncargos}
                operacao={operacao}
                form={form?.pagarConta}
                fieldsRoot="pagarConta"
                fixedDataMovimentacao={fixedDataMovimentacao}
                integradoPagamentoOnline={
                  dataEncargos?.tenantIntegradoPagamentoOnline
                }
                required={form?.liquidarConta || !!required}
                setValue={setValue}
              />
            ) : null}
          </InputToggleContent>
        )}
      />
    </Box>
  );
}

export default LiquidarConta;
